import React from 'react';
import { IAudition } from '../../types/global/helper';
import { Box, CardMedia, Chip, IconButton, Stack, Typography } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { theme } from '../../constants/theme';
import { convertMinuteSecondsDays, formatNumberWithK, pickAuditionCreatedTimestamp } from '../../utils/AppUtils';
import { CompanyAvatar } from '../Avatar/CompanyAvatar';
import { BackgroundColorCard } from '../Cards/BackGroundColorCard';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import moment from 'moment';

interface IAuditionCard {
    audition: IAudition;
    onViewAndApplyClick: () => void;
}

export const AuditionCard: React.FC<IAuditionCard> = ({ audition, onViewAndApplyClick }) => {
    const { themeStyle } = useThemeContext();
    const createdTs = pickAuditionCreatedTimestamp(audition);
    const postedTime = createdTs ? convertMinuteSecondsDays(createdTs) : '';
    const formattedDate = audition.job_start_date ? moment(audition.job_start_date).format('MMMM D, YYYY') : '';
    const formattedTime = audition.job_start_date
        ? moment(audition.job_start_date).format('h:mm A') + (audition.timezone ? ` ${audition.timezone}` : '')
        : '';

    return (
        <Box
            sx={{
                width: '100%',
                borderRadius: '20px',
                backgroundColor: '#131517',
                display: 'flex',
                flexDirection: 'column',
                p: { xs: '16px', sm: '20px 24px' },
                gap: '16px',
                transition: 'box-shadow 0.2s ease',
                '&:hover': {
                    boxShadow: `0 0 0 1px ${theme[themeStyle].primary_orange}22`,
                },
            }}>
            {/* Top: Company info + Posted time */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                <Stack
                    direction="row"
                    alignItems="center"
                    gap={1}
                    sx={{
                        display: 'inline-flex',
                        width: 'auto',
                        maxWidth: 'calc(100% - 120px)',
                        flexShrink: 1,
                        minWidth: 0,
                        borderRadius: '160px',
                        backgroundColor: 'rgba(251, 188, 85, 0.1)',
                        py: '4px',
                        px: '8px',
                    }}>
                    <Box sx={{ flexShrink: 0 }}>
                        <CompanyAvatar company={audition.production_company} enableDefaultNavigation size="small" />
                    </Box>
                    <Stack
                        direction="row"
                        alignItems="center"
                        gap={0.75}
                        sx={{ minWidth: 0, flex: '1 1 auto', overflow: 'hidden' }}>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 11,
                                color: theme[themeStyle].true_white,
                                lineHeight: 1.2,
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}>
                            {audition.production_company?.company_name}
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 10,
                                color: '#9ea5ad',
                                lineHeight: 1.2,
                                flexShrink: 0,
                            }}>
                            {formatNumberWithK(audition.production_company?.number_of_reviews || 0)}
                        </Typography>
                    </Stack>
                </Stack>
                <Stack direction="row" alignItems="center" gap={0.5} flexShrink={0}>
                    <AccessTimeIcon sx={{ fontSize: 14, color: '#9ea5ad' }} />
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 10,
                            color: '#9ea5ad',
                            fontStyle: 'italic',
                            whiteSpace: 'nowrap',
                        }}>
                        {postedTime ? `Posted ${postedTime} ago` : 'Recently posted'}
                    </Typography>
                </Stack>
            </Stack>

            {/* Image Section */}
            {audition.imageCover && (
                <Box sx={{ position: 'relative', borderRadius: '10px', overflow: 'hidden' }}>
                    <CardMedia
                        component="img"
                        image={audition.imageCover}
                        alt={audition.title}
                        sx={{ objectFit: 'cover', height: '220px', width: '100%' }}
                    />
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        sx={{ position: 'absolute', top: 12, left: 12, right: 12 }}>
                        <Stack direction="row" gap={0.5} flexWrap="wrap">
                            {audition.compensation && (
                                <Chip
                                    label={audition.compensation === 'paid' ? 'Paid' : audition.compensation}
                                    size="small"
                                    sx={{
                                        backgroundColor: '#24292e',
                                        color: '#fff',
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: 10,
                                        height: '24px',
                                    }}
                                />
                            )}
                            {audition.union_affiliation && (
                                <Chip
                                    label={audition.union_affiliation}
                                    size="small"
                                    sx={{
                                        backgroundColor: theme[themeStyle].primary_orange,
                                        color: '#fff',
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: 10,
                                        height: '24px',
                                    }}
                                />
                            )}
                            {audition.type_of_work && (
                                <Chip
                                    label={audition.type_of_work}
                                    size="small"
                                    sx={{
                                        backgroundColor: theme[themeStyle].highlight,
                                        color: '#fff',
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: 10,
                                        height: '24px',
                                    }}
                                />
                            )}
                        </Stack>
                        <IconButton
                            size="small"
                            sx={{
                                color: '#fff',
                                backgroundColor: 'rgba(0,0,0,0.3)',
                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.5)' },
                            }}>
                            {audition.has_user_bookmarked ? (
                                <BookmarkIcon sx={{ fontSize: 20 }} />
                            ) : (
                                <BookmarkBorderIcon sx={{ fontSize: 20 }} />
                            )}
                        </IconButton>
                    </Stack>
                </Box>
            )}

            {!audition.imageCover && audition.is_background_color && audition.background_color && (
                <BackgroundColorCard
                    backgroundColor={audition.background_color}
                    text={audition.title}
                    sx={{ height: '220px', width: '100%', my: 0, borderRadius: '10px' }}
                />
            )}

            {/* Info Section */}
            <Box sx={{ borderRadius: '8px', backgroundColor: 'rgba(46, 49, 52, 0.3)', p: '10px 12px' }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 15,
                        color: '#fff',
                        lineHeight: '22px',
                        mb: 1,
                        display: '-webkit-box',
                        overflow: 'hidden',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 2,
                    }}>
                    {audition.title}
                </Typography>
                <Stack gap={0.25}>
                    {formattedDate && (
                        <Stack direction="row" alignItems="center" gap={1} py={0.5}>
                            <CalendarTodayOutlinedIcon sx={{ fontSize: 16, color: '#ced2d6' }} />
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 13,
                                    color: '#ced2d6',
                                    lineHeight: '20px',
                                }}>
                                {formattedDate}
                            </Typography>
                        </Stack>
                    )}
                    {formattedTime && (
                        <Stack direction="row" alignItems="center" gap={1} py={0.5}>
                            <AccessTimeIcon sx={{ fontSize: 16, color: '#ced2d6' }} />
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 13,
                                    color: '#ced2d6',
                                    lineHeight: '20px',
                                }}>
                                {formattedTime}
                            </Typography>
                        </Stack>
                    )}
                    {audition.total_submissions > 0 && (
                        <Stack direction="row" alignItems="center" gap={1} py={0.5}>
                            <PeopleOutlineIcon sx={{ fontSize: 16, color: '#ced2d6' }} />
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 13,
                                    color: '#ced2d6',
                                    lineHeight: '20px',
                                }}>
                                {`Over ${audition.total_submissions} people applied`}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            </Box>

            {/* Apply Button */}
            <Box
                onClick={onViewAndApplyClick}
                sx={{
                    width: '100%',
                    borderRadius: '100px',
                    backgroundColor: theme[themeStyle].primary_orange,
                    border: `1px solid ${theme[themeStyle].primary_orange}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: '10px',
                    px: '18px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        backgroundColor: 'transparent',
                        '& .apply-text, & .apply-icon': {
                            color: theme[themeStyle].primary_orange,
                        },
                    },
                }}>
                <Stack direction="row" alignItems="center" gap={1}>
                    <Typography
                        className="apply-text"
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 14,
                            color: '#131517',
                            lineHeight: '20px',
                            transition: 'color 0.2s ease',
                        }}>
                        Apply now
                    </Typography>
                    <ArrowForwardIcon
                        className="apply-icon"
                        sx={{ fontSize: 18, color: '#131517', transition: 'color 0.2s ease' }}
                    />
                </Stack>
            </Box>
        </Box>
    );
};
